import React, { useState, useMemo, useEffect } from 'react';
import FormSetup from '../Forms/FormSetup';
import { MasterConfigs } from '../../interfaces/entity';
import { constants } from '../../constants/common';
import ProfileCard from '../ContactInfo/ProfileCard';
import { ReminderModal, type ReminderConfig } from '../Reminder';
import { useDispatch } from 'react-redux';
import { postData } from '../../actions/developer';
import { Button, Space } from 'antd';
import moment from 'moment';

interface ScheduleCallModalProps {
    open: boolean;
    onCancel: () => void;
    onSuccess?: () => void;
    recordId: string;
    modelName: string;
    initialValues?: any;
}

const ScheduleCallModal: React.FC<ScheduleCallModalProps> = ({
    open,
    onCancel,
    onSuccess,
    recordId,
    modelName,
    initialValues
}) => {
    const [reminderModalOpen, setReminderModalOpen] = useState(false);
    const [reminderConfig, setReminderConfig] = useState<ReminderConfig | null>(null);
    const [formInstance, setFormInstance] = useState<any>(null);
    const dispatch = useDispatch();

    // Map model names to refType
    const getRefType = (modelName: string): string => {
        const mapping: { [key: string]: string } = {
            'leads': 'Leads',
            'contacts': 'Contacts',
            'accounts': 'Businesses',
            'deals': 'Deals',
        };
        return mapping[modelName.toLowerCase()] || modelName;
    };

    const mappedRefType = useMemo(() => getRefType(modelName), [modelName]);

    const config: MasterConfigs = useMemo(() => ({
        api: () => `/call-schedule`,
        title: "Schedule Call",
        modalTitle: () => 'Schedule Call',
        modalProps: {
            maskClosable: false,
        },
        getFormColumns: () => {
            const formColumns: any[] = [
                {
                    label: 'Call Owner',
                    name: 'callOwner',
                    type: 'select',
                    isCombo: true,
                    rules: [{ required: true, message: 'Call Owner is required' }],
                },
                {
                    span: 16,
                },
                {
                    label: "Subject",
                    name: "subject",
                    type: "text",
                    span: 16,
                    rules: [{ required: true, message: 'Subject is required' }]
                },
                {
                    label: "Phone Number",
                    name: "phoneNumber",
                    type: "text",
                    span: 16,
                    rules: [{ required: true, message: 'Phone Number is required' }]
                },
                {
                    label: "Call Type",
                    name: "callType",
                    type: "select",
                    children: [
                        { name: 'Inbound', value: 'Inbound' },
                        { name: 'Outbound', value: 'Outbound' }
                    ],
                    commonProps: {
                        initialValue: 'Outbound'
                    }
                },
                {
                    label: "Scheduled Date",
                    name: "scheduledDate",
                    type: "date",
                    rules: [{ required: true, message: 'Scheduled Date is required' }]
                },
                {
                    label: "Duration (minutes)",
                    name: "duration",
                    type: "number",
                    commonProps: {
                        initialValue: 30
                    }
                },
                {
                    span: 16,
                },
                {
                    label: "Description",
                    name: "description",
                    type: "textarea",
                    span: 16
                },
                {
                    label: "Reminder",
                    name: "reminder",
                    type: "custom",
                    span: 24,
                    render: (form) => {
                        return (
                            <div>
                                <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <span style={{ fontWeight: 500 }}>Reminder</span>
                                    <Button
                                        type={reminderConfig ? 'default' : 'primary'}
                                        size="small"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setReminderModalOpen(true);
                                        }}
                                    >
                                        {reminderConfig ? 'Edit Reminder' : 'Add Reminder'}
                                    </Button>
                                </Space>
                                {reminderConfig && (
                                    <div style={{ fontSize: 12, color: '#666', marginTop: 4, padding: '8px', background: '#f5f5f5', borderRadius: 4 }}>
                                        {formatReminderText(reminderConfig, form.getFieldValue('scheduledDate'))}
                                    </div>
                                )}
                            </div>
                        );
                    }
                }
            ];

            return formColumns;
        },
        combo: [
            {
                api: '/users',
                name: 'callOwner',
                idKey: '_id',
                render: (text) => <ProfileCard
                    size={30}
                    name={constants.GET_FULL_NAME(text)}
                    avatarUrl={text?.avatar ? process.env.FILE_URL + text?.avatar : null}
                    role={null}
                />
            }
        ],
        onSubmit: async (id: string | null, values: any) => {
            const callData: any = {
                ...values,
                refType: mappedRefType,
                refId: recordId,
            };

            // Add reminder config if configured
            if (reminderConfig) {
                callData.reminder = reminderConfig;
            }

            const apiUrl = `/call-schedule`;
            const resData = await dispatch(postData(apiUrl, callData));

            if (resData && resData.code === 200) {
                setReminderConfig(null);
                if (onSuccess) {
                    onSuccess();
                }
            }

            return resData;
        },
        onError: (error: any) => {
            console.error('Error creating scheduled call:', error);
        },
        afterSubmit: () => {
            onCancel();
        }
    }), [recordId, modelName, mappedRefType, reminderConfig, formInstance, dispatch]);

    const formatReminderText = (config: ReminderConfig, scheduledDate?: Date | string | null): string => {
        if (!config) return '';
        const timeStr = moment(config.time, 'HH:mm').format('hh:mm A');
        const alertStr = config.alertType === 'both' ? 'Email and Pop-up' : config.alertType === 'email' ? 'Email' : 'Pop-up';

        if (config.mode === 'before') {
            const days = config.daysBefore === 0 ? 'scheduled date' : `${config.daysBefore} day(s) before scheduled date`;
            return `On ${days} at ${timeStr} by ${alertStr}`;
        } else {
            return `On scheduled date at ${timeStr} by ${alertStr}`;
        }
    };

    // Set initial form values when form instance is available
    useEffect(() => {
        if (formInstance && mappedRefType) {
            formInstance.setFieldsValue({
                refType: mappedRefType,
                refId: recordId,
                ...initialValues
            });
        }
    }, [formInstance, mappedRefType, recordId, initialValues]);

    return (
        <>
            <FormSetup
                config={config}
                open={open}
                onClose={onCancel}
                isModal={true}
                form={(form) => {
                    setFormInstance(form);
                }}
            />

            {/* Reminder Modal */}
            <ReminderModal
                open={reminderModalOpen}
                onClose={() => setReminderModalOpen(false)}
                onSave={(config) => {
                    setReminderConfig(config);
                }}
                initialValue={reminderConfig || undefined}
                dueDate={formInstance?.getFieldValue('scheduledDate')}
            />
        </>
    );
};

export default ScheduleCallModal;
